import React, { useEffect, useRef, useState } from 'react';
// import { reactPlugin } from '../../../appInsights';
// import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { jsonEditorStyles } from '../jsonEditorStyles';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import cn from 'clsx';
import useComponentUIHook from './useComponentUIHook';
import { TextComponent, SelectComponent, TooltipComponent } from '../../uiComponents';
import { SelectChangeEvent } from '@mui/material';
import { OptionProps } from '../../../view-models';
import { cloneDeep } from 'lodash';
import DeleteIcon from '@mui/icons-material/Delete';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import SessionManagement from './sessionManagement';
import jsonComponents from '../../../constants/jsonComponents';
import AddOrEditSubflowUI from './addOrEditSubflow';
import useJSONEditorHook from '../useJSONEditorHook';
import { FieldMapperRow } from './fieldMapperRow';
import TemplatizedFieldComponent, { getTemplatizedValue, TemplatizedValue } from './templatizedFieldComponent';

const styles = makeStyles((theme: Theme) =>
  createStyles({
    icon: {
      cursor: 'pointer',
      marginLeft: '0.2rem',
      marginTop: '0.8rem',
    },
    flexRow: {
      display: 'flex',
      flexDirection: 'row',
    },
  }),
);

interface componentProps {
  isUserInfoEndpoint: boolean;
  componentData: any;
  closeGritComponent: any;
  setUnsavedChanges: any;
  openUserInfoEndpointFC?: any;
}
const GenerateTokenIssuerUI = ({
  isUserInfoEndpoint,
  componentData,
  closeGritComponent,
  setUnsavedChanges,
  openUserInfoEndpointFC,
}: componentProps) => {
  const { modifiedData, setModifiedData, checkIfSameIgnoringSubFlows, allClaims, onDeleteSubFlow, setModifiedTemplatizations, modifiedTemplatizations } =
    useComponentUIHook(componentData);
  const { setCurrentTabId } = useJSONEditorHook();
  const initAttribute = useRef(componentData['attribute']);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [ssoSecondsErrorText, setSSOSecondsErrorText] = useState<string>('');
  const classes = jsonEditorStyles();
  const classes1 = styles();
  const configData = jsonComponents.tokenIssuer;

  useEffect(() => {
    const _res: boolean = checkIfSameIgnoringSubFlows(['userInfoEndpoint']);
    const _ssoSecondsError = modifiedData['ssoExpirySeconds'] && (!modifiedData['ssoExpirySeconds'].templatized) && (
      (!(/^\d+$/.test(modifiedData['ssoExpirySeconds'].value))) ||
      (parseInt(modifiedData['ssoExpirySeconds'].value) < 900) ||
      (parseInt(modifiedData['ssoExpirySeconds'].value) > 86400)
    );
    if (_ssoSecondsError) {
      setSSOSecondsErrorText("Value must be an integer between 900 and 86400 seconds");
    } else {
      setSSOSecondsErrorText("");
    }
    setDisableSubmit(_res || ((modifiedData['protocol'] === 'saml') && (!modifiedData['issuerUri'])) || _ssoSecondsError);
    setUnsavedChanges(_res);
  }, [modifiedData]);

  const submitData = (data: any, addUserInfoEndpoint: boolean, subFlowType?: string) => {
    setDisableSubmit(false);
    closeGritComponent(data, modifiedTemplatizations, addUserInfoEndpoint, subFlowType);
  };

  const onDeleteHandler = (index: number) => {
    const _attributes = modifiedData['attributes'].reduce((acc: any[], item: any, ind: number) => {
      return ind === index ? acc : acc.concat(item);
    }, []);
    setModifiedData({
      ...modifiedData,
      attributes: _attributes,
    });
  };

  return (
    <div className={classes.flexCol}>
      {!isUserInfoEndpoint && (
        <>
          <div className={classes1.flexRow}>
            <SelectComponent
              changeHandler={(e: SelectChangeEvent<any>) => {
                setModifiedData({
                  ...modifiedData,
                  protocol: e.target.value,
                });
              }}
              data={{
                value: modifiedData['protocol'] ? modifiedData['protocol'] : 'oidc',
                label: 'Protocol',
                style: { margin: 1, width: '200px' },
                options: [
                  { value: 'oidc', label: 'OpenIdConnect' },
                  { value: 'saml', label: 'SAML' },
                ],
              }}
            />
            {(modifiedData['protocol'] === 'saml') && (
              <TemplatizedFieldComponent
                modifiedTemplatizations={modifiedTemplatizations}
                setModifiedTemplatizations={setModifiedTemplatizations}
                data={{
                  fieldPath: 'issuerUri',
                  label: 'Issuer URI',
                  value: getTemplatizedValue(modifiedData['issuerUri']),
                  style: { marginLeft: 1, width: 600 },
                  required: true,
                  variant: 'text'
                }}
                changeHandler={(e: TemplatizedValue) => {
                  setModifiedData({
                    ...modifiedData,
                    issuerUri: { ...e }
                  });
                }}
              />
            )}
          </div>
          <div className={classes1.flexRow}>
            <TemplatizedFieldComponent
              modifiedTemplatizations={modifiedTemplatizations}
              setModifiedTemplatizations={setModifiedTemplatizations}
              data={{
                fieldPath: 'ssoScope',
                label: 'SSO Session Scope',
                value: getTemplatizedValue(modifiedData['ssoScope']) ?? 'Tenant',
                style: { marginLeft: 1, width: 600 },
                variant: 'select',
                manualOptions: [
                  { value: 'Tenant', label: 'Tenant'},
                  { value: 'Application', label: 'Application'},
                  { value: 'Policy', label: 'Policy' },
                  { value: 'Suppressed', label: 'Suppressed'}
                ]
              }}
              changeHandler={(e: TemplatizedValue) => {
                setModifiedData({
                  ...modifiedData,
                  ssoScope: { ...e }
                });
              }}
            />
            <TooltipComponent
              title='SSO Session Scope'
              desc='Defines the scope of SSO sessions for this policy. Tenant scope will share SSO sessions from this policy across all policies on the tenant. Application scope will share SSO sessions across all policies called by the same application. Policy scope will limit the SSO session to this policy. Suppressed disables SSO session behavior for this policy.'
            />
          </div>
          <div className={classes1.flexRow}>
            <TemplatizedFieldComponent
              modifiedTemplatizations={modifiedTemplatizations}
              setModifiedTemplatizations={setModifiedTemplatizations}
              data={{
                fieldPath: 'ssoExpiryType',
                label: 'SSO Expiry Type',
                value: getTemplatizedValue(modifiedData['ssoExpiryType']) ?? 'Absolute',
                style: { marginLeft: 1, width: 600 },
                variant: 'select',
                manualOptions: [
                  { value: 'Absolute', label: 'Absolute' },
                  { value: 'Rolling', label: 'Rolling'}
                ]
              }}
              changeHandler={(e: TemplatizedValue) => {
                setModifiedData({
                  ...modifiedData,
                  ssoExpiryType: { ...e }
                });
              }}
            />
            <TooltipComponent
              title='SSO Session Expiry Type'
              desc='An "Absolute" expiry type means that the user will be forced to reauthenticate after the session expiry time period. A "Rolling" expiry type means that the user will remain signed in as long as they are continually active in the application.'
            />
          </div>
          <div className={classes1.flexRow}>
            <TemplatizedFieldComponent
              modifiedTemplatizations={modifiedTemplatizations}
              setModifiedTemplatizations={setModifiedTemplatizations}
              data={{
                fieldPath: 'ssoExpirySeconds',
                label: 'SSO Expiry Time',
                value: getTemplatizedValue(modifiedData['ssoExpirySeconds']) ?? '86400',
                style: { marginLeft: 1, width: 600 },
                variant: 'text',
                error: !!ssoSecondsErrorText,
                helperText: ssoSecondsErrorText
              }}
              changeHandler={(e: TemplatizedValue) => {
                setModifiedData({
                  ...modifiedData,
                  ssoExpirySeconds: { ...e }
                });
              }}
            />
            <TooltipComponent
              title='SSO Expiry Time'
              desc='The lifetime of the SSO session cookie for this policy, in seconds. The minimum value is 900 seconds, and the maximum is 86400 (24 hours).'
            />
          </div>
        </>
      )}
      {modifiedData['attributes'].map((attr: any, index: number) => (
        <FieldMapperRow
          key={index}
          rowData={{ ...attr }}
          writeback={(rowData: any) => {
            const attrs = cloneDeep(modifiedData['attributes']);
            attrs[index] = { ...rowData };
            setModifiedData({
              ...modifiedData,
              attributes: attrs,
            });
          }}
          claimsList={Object.keys(allClaims).map((clm: any) => {
            return { label: clm, value: clm };
          })}
          allClaims={allClaims}
          enabledSources={['literal', 'resolver']}
          precursorFields={[
            {
              fieldName: 'attributeName',
              fieldType: 'select',
              fieldData: {
                value: attr['attributeName'],
                label: 'Select Attribute',
                style: { margin: 1, width: '300px' },
                options: Object.keys(allClaims).reduce(
                  (acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }),
                  [],
                ),
              },
            },
          ]}
          sourceTypeFieldName="defaultValueSource"
          sourceTypeFieldLabel="Default Value Source"
          sourceFieldName="defaultValue"
          sourceFieldLabels={{ literal: 'Default Value', resolver: 'Session Context' }}
          partnerTypeFieldLabel="Partner Claim Type"
          partnerTypeFieldName="partnerClaimType"
          buttonText="Delete"
          buttonAction={() => {
            window.confirm('Are you sure you want to delete ' + attr['attributeName'] + '? This cannot be undone.') &&
              onDeleteHandler(index);
          }}
        />
      ))}
      {/*<div className={classes.flexRow} key={index}>
          <SelectComponent
            changeHandler={(e: SelectChangeEvent<any>) => {
              const attrs = cloneDeep(modifiedData['attributes']);
              attrs[index]['attributeName'] = e.target.value;
              setModifiedData({
                ...modifiedData,
                attributes: attrs,
              });
            }}
            data={{
              value: attr['attributeName'],
              label: 'Select Attribute',
              style: { margin: 1, width: '300px' },
              options: Object.keys(allClaims).reduce(
                (acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }),
                [],
              ),
            }}
          />
          <TextComponent
            changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
              const attrs = cloneDeep(modifiedData['attributes']);
              attrs[index]['defaultValue'] = e.target.value;
              setModifiedData({
                ...modifiedData,
                attributes: attrs,
              });
            }}
            data={{
              value: attr['defaultValue'],
              label: 'Default Value',
              style: { margin: 1, width: '300px' },
            }}
          />
          <div className={classes1.icon}>
            <DeleteIcon
              onClick={() => {
                window.confirm(
                  'Are you sure you want to delete ' + attr['attributeName'] + '? This cannot be undone.',
                ) && onDeleteHandler(index);
              }}
              color="error"
            />
          </div>
            </div>*/}
      <FieldMapperRow
        rowData={{ ...modifiedData['attribute'] }}
        writeback={(rowData: any) => {
          setModifiedData({
            ...modifiedData,
            attribute: { ...rowData },
          });
        }}
        claimsList={Object.keys(allClaims).map((clm: any) => {
          return { label: clm, value: clm };
        })}
        allClaims={allClaims}
        enabledSources={['literal', 'resolver']}
        precursorFields={[
          {
            fieldName: 'attributeName',
            fieldType: 'select',
            fieldData: {
              value: modifiedData['attribute']['attributeName'],
              label: 'Select Attribute',
              style: { margin: 1, width: '300px' },
              options: Object.keys(allClaims).reduce(
                (acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }),
                [],
              ),
            },
          },
        ]}
        sourceTypeFieldName="defaultValueSource"
        sourceTypeFieldLabel="Default Value Source"
        sourceFieldName="defaultValue"
        sourceFieldLabels={{ literal: 'Default Value', resolver: 'Session Context' }}
        partnerTypeFieldLabel="Partner Claim Type"
        partnerTypeFieldName="partnerClaimType"
        buttonText="Add"
        buttonAction={() => {
          setModifiedData({
            ...modifiedData,
            attributes: [...modifiedData['attributes'], modifiedData['attribute']],
            attribute: initAttribute.current,
            subClaim:
              modifiedData['attributes'].length === 0
                ? modifiedData['attribute']['attributeName']
                : modifiedData['attributes'][0]['attributeName'],
          });
        }}
      />
      {/*<div className={cn(classes.flexRow, classes.alignCenter)}>
        <SelectComponent
          changeHandler={(e: SelectChangeEvent<any>) => {
            setModifiedData({
              ...modifiedData,
              attribute: { ...modifiedData['attribute'], attributeName: e.target.value },
            });
          }}
          data={{
            value: modifiedData['attribute']['attributeName'],
            label: 'Select Attribute',
            style: { margin: 1, width: '300px' },
            options: Object.keys(allClaims).reduce(
              (acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }),
              [],
            ),
          }}
        />
        <TextComponent
          changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            setModifiedData({
              ...modifiedData,
              attribute: {
                ...modifiedData['attribute'],
                defaultValue: e.target.value,
              },
            });
          }}
          data={{
            value: modifiedData['attribute']['defaultValue'],
            label: 'Default Value',
            style: { margin: 1, width: '300px' },
          }}
        />
        <div>
          <Button
            type="submit"
            variant="contained"
            sx={{ ml: 1 }}
            disabled={!modifiedData['attribute']['attributeName']}
            onClick={() => {
              setModifiedData({
                ...modifiedData,
                attributes: [...modifiedData['attributes'], modifiedData['attribute']],
                attribute: initAttribute.current,
                subClaim:
                  modifiedData['attributes'].length === 0
                    ? modifiedData['attribute']['attributeName']
                    : modifiedData['attributes'][0]['attributeName'],
              });
            }}
          >
            Add
          </Button>
        </div>
          </div>*/}

      {!isUserInfoEndpoint && (
        <div className={classes1.flexRow}>
          <SelectComponent
            changeHandler={(e: SelectChangeEvent<any>) => {
              setModifiedData({ ...modifiedData, subClaim: e.target.value });
            }}
            data={{
              value: modifiedData['subClaim'],
              label: 'Sub Claim',
              style: { margin: 1, width: '300px' },
              options: modifiedData['attributes'].reduce(
                (acc: OptionProps[], curVal: any) =>
                  acc.concat({ label: curVal['attributeName'], value: curVal['attributeName'] }),
                [],
              ),
            }}
          />
        </div>
      )}
      {configData.sessionManagement && (
        <SessionManagement
          modifiedData={modifiedData}
          changeHandler={(data: any) => setModifiedData(data)}
          sessionTypes="none"
        />
      )}
      <div>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={disableSubmit}
          sx={{ mt: 3, mb: 2 }}
          onClick={() => {
            closeGritComponent(cloneDeep(modifiedData), modifiedTemplatizations);
          }}
        >
          Save
        </Button>
      </div>
      {!isUserInfoEndpoint && (!modifiedData['protocol'] || modifiedData['protocol'] === 'oidc') && (
        <AddOrEditSubflowUI
          modifiedData={modifiedData}
          submitData={(modifiedData: any, csf: boolean, subflowType?: string) =>
            submitData(cloneDeep(modifiedData), csf, subflowType)
          }
          openSubFlowFC={openUserInfoEndpointFC}
          setCurrentTabId={setCurrentTabId}
          onDeleteSubFlow={onDeleteSubFlow}
          subFlowType="userInfoEndpoint"
          subFlowDisplayName="User Info Endpoint"
          disableSubmit={disableSubmit}
        />
      )}
    </div>
  );
};
// export default withAITracking(reactPlugin, GenerateTokenIssuerUI);
export default GenerateTokenIssuerUI;
